'use client'

import type {ReactNode} from 'react'
import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {IntlErrorCode, NextIntlClientProvider} from 'next-intl'
import {createDateTimeFormatter, DEFAULT_FITRO_TIME_ZONE} from '@fitro/date-time'
import {formatters, getBrowserLocale, getLocaleConfig, isValidLocale, loadMessages, storeLocalePreference} from '../../lib/i18n'
import {debug, logClientWarn} from '../../lib/client-logger'
import type {CurrencyFormatOptions, I18nContextValue, LocaleCode, LocaleConfig, Messages, NumberFormatOptions, TranslationFunction, TranslationParams} from '../../types/i18n/core'

const isDebugEnv = process.env.NEXT_PUBLIC_APP_ENV === 'dev' || process.env.NODE_ENV === 'development'

const I18nContext = createContext<I18nContextValue | undefined>(undefined)

const getCustomMessageFallback = ({namespace, key, error}: any) => {
    const path = [namespace, key].filter(Boolean).join('.')

    if (error.code === IntlErrorCode.MISSING_MESSAGE) {
        return path
    } else {
        return `[Error: ${path}]`
    }
}

const INTL_FORMATS = {
    dateTime: {
        short: {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        },
        long: {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            weekday: 'long'
        }
    },
    number: {
        currency: {
            style: 'currency',
            currency: 'PLN'
        }
    }
} as const

interface I18nProviderProps {
    children: ReactNode
    defaultLocale?: LocaleCode
    messages?: Partial<Record<LocaleCode, Messages>>
    storageKey?: string
    timeZone?: string
}

export const I18nProvider: React.FC<I18nProviderProps> = ({
                                                              children,
                                                              defaultLocale = 'pl',
                                                              messages: initialMessages,
                                                              storageKey = 'fitro-locale',
                                                              timeZone = DEFAULT_FITRO_TIME_ZONE
                                                          }) => {
    const defaultMessages = initialMessages?.[defaultLocale] ?? {}
    const [locale, setLocaleState] = useState<LocaleCode>(defaultLocale)
    const [messages, setMessages] = useState<Messages>(defaultMessages)
    const [mounted, setMounted] = useState(false)
    const [isLoading, setIsLoading] = useState(!initialMessages?.[defaultLocale])
    // A default locale is not a choice: storing it would keep a later browser language or choice from applying.
    const persistLocale = React.useRef(true)

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const detectionResult = getBrowserLocale()
            const initialLocale = detectionResult.locale
            persistLocale.current = detectionResult.source !== 'default'
            setLocaleState(initialLocale)
            if (initialMessages?.[initialLocale]) {
                setMessages(initialMessages[initialLocale])
                setIsLoading(false)
            }
            setMounted(true)
        }
    }, [initialMessages])

    useEffect(() => {
        if (!mounted) return

        const loadLocaleMessages = async () => {
            try {
                if (initialMessages && initialMessages[locale]) {
                    setMessages(initialMessages[locale])
                    setIsLoading(false)
                } else {
                    setIsLoading(true)
                    const localeMessages = await loadMessages(locale)
                    setMessages(localeMessages)
                    setIsLoading(false)
                }
            } catch (error) {
                logClientWarn({
                    event: 'i18n_load_failed',
                    source: 'i18n-provider',
                    message: `Failed to load messages for locale '${locale}'.`,
                    context: {locale},
                    error,
                })
                setMessages({})
                setIsLoading(false)
            }
        }

        loadLocaleMessages()
    }, [locale, mounted, initialMessages])

    useEffect(() => {
        if (mounted) {
            if (persistLocale.current) storeLocalePreference(locale)

            if (typeof document !== 'undefined') {
                document.documentElement.lang = locale
            }
        }
    }, [locale, mounted, storageKey])

    const t: TranslationFunction = React.useCallback((key: string, paramsOrFallback?: TranslationParams | string, maybeFallback?: string) => {
        const params = typeof paramsOrFallback === 'object' && paramsOrFallback !== null
            ? paramsOrFallback as TranslationParams
            : undefined
        const fallback = typeof paramsOrFallback === 'string' ? paramsOrFallback : maybeFallback

        const getValue = (obj: Messages, path: string): string | undefined => {
            const keys = path.split('.')
            let current = obj

            for (const k of keys) {
                if (current && typeof current === 'object' && k in current) {
                    current = current[k] as Messages
                } else {
                    return undefined
                }
            }

            return typeof current === 'string' ? current : undefined
        }

        let translatedText = getValue(messages, key) ?? fallback

        if (!translatedText) {
            return fallback ?? key
        }

        if (params) {
            try {
                Object.entries(params).forEach(([paramKey, value]) => {
                    translatedText = translatedText!.replace(new RegExp(`{${paramKey}}`, 'g'), String(value))
                })
            } catch {
                return translatedText
            }
        }

        return translatedText
    }, [messages])

    const changeLocale = useCallback(async (newLocale: LocaleCode) => {
        if (!isValidLocale(newLocale)) {
            logClientWarn({
                event: 'i18n_invalid_locale',
                source: 'i18n-provider',
                message: `Rejected invalid locale '${newLocale}'.`,
                context: {locale: newLocale},
            })
            return
        }

        persistLocale.current = true
        if (newLocale !== locale) {
            setLocaleState(newLocale)
        } else {
            storeLocalePreference(newLocale)
        }
    }, [locale])

    // Keyed on the effective locale, not on `mounted`: the mount effect must not hand hydrating consumers a new context
    // value when the locale stays the same, or React switches their Suspense boundaries to client rendering.
    const activeLocale = mounted ? locale : defaultLocale
    const dateTime = useMemo(
        () => createDateTimeFormatter({locale: activeLocale, timeZone}),
        [activeLocale, timeZone]
    )

    const formatNumber = useCallback((num: number, options?: NumberFormatOptions) => {
        return formatters.number(num, options?.locale || locale, options)
    }, [locale])

    const formatCurrency = useCallback((amount: number, options?: CurrencyFormatOptions) => {
        const currency = options?.currency || 'PLN'
        return formatters.currency(amount, options?.locale || locale, currency)
    }, [locale])

    const isLocaleSupported = useCallback((localeToCheck: string): localeToCheck is LocaleCode => {
        return isValidLocale(localeToCheck)
    }, [])

    const getLocaleConfiguration = useCallback((localeCode: LocaleCode): LocaleConfig | undefined => {
        return getLocaleConfig(localeCode)
    }, [])

    const missingTranslationsRef = React.useRef<Set<string>>(new Set())
    const logTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

    const handleTranslationError = React.useCallback((error: any) => {
        if (error.code === IntlErrorCode.MISSING_MESSAGE) {
            const path = [error.namespace, error.key].filter(Boolean).join('.')
            missingTranslationsRef.current.add(path)

            if (isDebugEnv && !logTimeoutRef.current) {
                logTimeoutRef.current = setTimeout(() => {
                    if (missingTranslationsRef.current.size > 0) {
                        debug(
                            'Missing translation keys detected',
                            {keys: Array.from(missingTranslationsRef.current).sort().join(',')},
                            'i18n-provider'
                        )
                        missingTranslationsRef.current.clear()
                    }
                    logTimeoutRef.current = null
                }, 2000)
            }
        } else {
            logClientWarn({
                event: 'i18n_translation_error',
                source: 'i18n-provider',
                message: 'Unexpected translation runtime error.',
                error,
            })
        }
    }, [])

    React.useEffect(() => {
        return () => {
            if (logTimeoutRef.current) {
                clearTimeout(logTimeoutRef.current)
            }
        }
    }, [])

    const contextValue = useMemo((): I18nContextValue => ({
        locale: activeLocale,
        messages,
        t,
        changeLocale,
        dateTime,
        formatNumber,
        formatCurrency,
        isLocaleSupported,
        getLocaleConfig: getLocaleConfiguration
    }), [
        activeLocale,
        messages,
        t,
        changeLocale,
        dateTime,
        formatNumber,
        formatCurrency,
        isLocaleSupported,
        getLocaleConfiguration
    ])

    // One stable set of props: a new next-intl context after mount would reach hydrating consumers as an update.
    return (
        <I18nContext.Provider value={contextValue}>
            <NextIntlClientProvider
                locale={mounted && !isLoading ? locale : defaultLocale}
                messages={messages}
                timeZone={timeZone}
                onError={handleTranslationError}
                getMessageFallback={getCustomMessageFallback}
                formats={INTL_FORMATS}
            >
                {children}
            </NextIntlClientProvider>
        </I18nContext.Provider>
    )
}

export const useI18n = () => {
    const context = useContext(I18nContext)
    if (context === undefined) {
        throw new Error('useI18n must be used within an I18nProvider')
    }
    return context
}

/** Reuses the active locale and messages; only the studio owns the timezone. */
export function StudioTimeProvider({timeZone, children}: {timeZone: string; children: ReactNode}) {
    const parent = useI18n();
    const dateTime = useMemo(() => createDateTimeFormatter({locale: parent.locale, timeZone}), [parent.locale, timeZone]);
    const value = useMemo(() => ({...parent, dateTime}), [parent, dateTime]);
    return <I18nContext.Provider value={value}><NextIntlClientProvider locale={parent.locale} messages={parent.messages as any} timeZone={timeZone}>{children}</NextIntlClientProvider></I18nContext.Provider>;
}
